import { Header, notify } from "@general-backoffice/core";
import type { CryptoPaymentConfig, CryptoPaymentConfigInput, CryptoPaymentOptions } from "@investhinks/api-client";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  CustomInput,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { cryptoPaymentConfigEndpoints } from "../../../../api";
import {
  DEFAULT_CHAIN_IDS,
  DEFAULT_CHAINS,
  DEFAULT_CURRENCIES,
  DEFAULT_CURRENCY_IDS,
  DEFAULT_MINIMUM_AMOUNT,
} from "../../../../constants/cryptoPaymentDefaults";

// Form data type
interface FormData {
  isEnabled: boolean;
  walletAddress: string;
  supportedChains: string[];
  supportedCurrencies: string[];
  minimumAmount: number | string;
  notes: string;
}

const CryptoPaymentSettings: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "cryptoPayments" });

  // Config state
  const [config, setConfig] = useState<CryptoPaymentConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    isEnabled: true,
    walletAddress: "",
    supportedChains: DEFAULT_CHAIN_IDS,
    supportedCurrencies: DEFAULT_CURRENCY_IDS,
    minimumAmount: DEFAULT_MINIMUM_AMOUNT,
    notes: "",
  });

  // Preview state (includes QR code)
  const [preview, setPreview] = useState<CryptoPaymentOptions | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);

  // Password confirmation state (required when changing wallet address)
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data } = await cryptoPaymentConfigEndpoints.get();

      if (data) {
        setConfig(data);
        setFormData({
          isEnabled: data.isEnabled ?? true,
          walletAddress: data.walletAddress || "",
          supportedChains: data.supportedChains || DEFAULT_CHAIN_IDS,
          supportedCurrencies: data.supportedCurrencies || DEFAULT_CURRENCY_IDS,
          minimumAmount: data.minimumAmount || DEFAULT_MINIMUM_AMOUNT,
          notes: data.notes || "",
        });
      }

      // Also load preview
      await loadPreview();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : t("loadError", "Failed to load crypto payment configuration");
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loadPreview = async () => {
    try {
      setPreviewLoading(true);
      // Get the options which includes the dynamically generated QR code
      const { data } = await cryptoPaymentConfigEndpoints.getOptions();
      if (data) {
        setPreview(data);
      }
    } catch (err: unknown) {
      console.warn("Failed to load preview:", err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleChainToggle = useCallback((chainId: string) => {
    setFormData((prev) => {
      const chains = prev.supportedChains.includes(chainId)
        ? prev.supportedChains.filter((c) => c !== chainId)
        : [...prev.supportedChains, chainId];
      return { ...prev, supportedChains: chains };
    });
  }, []);

  const handleCurrencyToggle = useCallback((currencyId: string) => {
    setFormData((prev) => {
      const currencies = prev.supportedCurrencies.includes(currencyId)
        ? prev.supportedCurrencies.filter((c) => c !== currencyId)
        : [...prev.supportedCurrencies, currencyId];
      return { ...prev, supportedCurrencies: currencies };
    });
  }, []);

  // Validate form data before saving
  const validateForm = (): boolean => {
    if (!formData.walletAddress || formData.walletAddress.trim().length < 10) {
      setError(t("validation.walletRequired", "A valid wallet address is required"));
      return false;
    }

    if (formData.supportedChains.length === 0) {
      setError(t("validation.chainsRequired", "At least one blockchain must be selected"));
      return false;
    }

    if (formData.supportedCurrencies.length === 0) {
      setError(t("validation.currenciesRequired", "At least one currency must be selected"));
      return false;
    }

    const minAmount = parseFloat(String(formData.minimumAmount));
    if (isNaN(minAmount) || minAmount < 0) {
      setError(t("validation.minimumAmount", "Minimum amount must be a positive number"));
      return false;
    }

    return true;
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    // If wallet address is changing, require password confirmation
    if (isWalletAddressChanging) {
      setShowPasswordModal(true);
      setPassword("");
      setPasswordError(null);
      return;
    }

    // No wallet change, save directly
    await saveConfig();
  };

  // Save config (called directly or after password confirmation)
  const saveConfig = async (passwordValue: string | null = null) => {
    const minAmount = parseFloat(String(formData.minimumAmount));

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(false);

      const updateData: CryptoPaymentConfigInput & { password?: string } = {
        isEnabled: formData.isEnabled,
        walletAddress: formData.walletAddress.trim(),
        supportedChains: formData.supportedChains,
        supportedCurrencies: formData.supportedCurrencies,
        minimumAmount: minAmount,
        notes: formData.notes?.trim() || undefined,
      };

      // Include password if provided (for wallet address changes)
      if (passwordValue) {
        updateData.password = passwordValue;
      }

      await cryptoPaymentConfigEndpoints.update(updateData);

      // Update local config state
      const updatedConfig: CryptoPaymentConfig = {
        ...(config || {}),
        isEnabled: formData.isEnabled,
        walletAddress: formData.walletAddress.trim(),
        supportedChains: formData.supportedChains,
        supportedCurrencies: formData.supportedCurrencies,
        minimumAmount: minAmount,
        notes: formData.notes?.trim(),
      };
      setConfig(updatedConfig);
      setSuccess(true);
      setShowPasswordModal(false);
      setPassword("");
      notify.success(t("saveSuccess", "Crypto payment settings saved successfully"));

      // Reload preview with new QR code
      await loadPreview();

      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      let errorMsg = String(t("saveError", "Failed to save crypto payment settings"));
      if (err instanceof Error) {
        errorMsg = err.message;
      } else if (err && typeof err === "object") {
        // Try to extract nested error message from API response
        if (
          "response" in err &&
          err.response &&
          typeof err.response === "object" &&
          "data" in err.response &&
          err.response.data &&
          typeof err.response.data === "object" &&
          "error" in err.response.data &&
          err.response.data.error &&
          typeof err.response.data.error === "object" &&
          "message" in err.response.data.error &&
          typeof err.response.data.error.message === "string"
        ) {
          errorMsg = err.response.data.error.message;
        } else if ("message" in err && typeof err.message === "string") {
          errorMsg = err.message;
        }
      }
      if (showPasswordModal) {
        setPasswordError(errorMsg);
      } else {
        setError(errorMsg);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordConfirm = async () => {
    if (!password) {
      setPasswordError(t("password.required", "Password is required"));
      return;
    }
    await saveConfig(password);
  };

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setPassword("");
    setPasswordError(null);
  };

  const handleReset = () => {
    if (config) {
      setFormData({
        isEnabled: config.isEnabled ?? true,
        walletAddress: config.walletAddress || "",
        supportedChains: config.supportedChains || DEFAULT_CHAIN_IDS,
        supportedCurrencies: config.supportedCurrencies || DEFAULT_CURRENCY_IDS,
        minimumAmount: config.minimumAmount || DEFAULT_MINIMUM_AMOUNT,
        notes: config.notes || "",
      });
    }
    setError(null);
    setSuccess(false);
  };

  // Allow saving if config doesn't exist yet (first-time setup) or if there are changes
  const hasChanges =
    !config ||
    formData.isEnabled !== config.isEnabled ||
    formData.walletAddress !== (config.walletAddress || "") ||
    JSON.stringify(formData.supportedChains) !== JSON.stringify(config.supportedChains || []) ||
    JSON.stringify(formData.supportedCurrencies) !== JSON.stringify(config.supportedCurrencies || []) ||
    parseFloat(String(formData.minimumAmount)) !== (config.minimumAmount || DEFAULT_MINIMUM_AMOUNT) ||
    (formData.notes || "") !== (config.notes || "");

  // Check if wallet address is being changed (requires password)
  const isWalletAddressChanging =
    formData.walletAddress.trim() !== (config?.walletAddress || "") && formData.walletAddress.trim().length > 0;

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12}>
          <h2 className="text-white mb-0">
            <i className="fab fa-ethereum mr-3"></i>
            {t("title", "Crypto Payment Settings")}
          </h2>
        </Col>
      </Header.BreadCrumb>

      <Container className="mt--6" fluid>
        <Row>
          {/* Configuration Form */}
          <Col xl={8}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">{t("configuration", "Configuration")}</h3>
                <CustomInput
                  type="switch"
                  id="isEnabled"
                  name="isEnabled"
                  label={formData.isEnabled ? t("enabled", "Enabled") : t("disabled", "Disabled")}
                  checked={formData.isEnabled}
                  onChange={handleInputChange}
                  disabled={isLoading || isSaving}
                />
              </CardHeader>
              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-4">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    {error}
                    <button type="button" className="close" onClick={() => setError(null)}>
                      <span>&times;</span>
                    </button>
                  </Alert>
                )}

                {success && (
                  <Alert color="success" className="mb-4">
                    <i className="fas fa-check-circle mr-2"></i>
                    {t("saveSuccess", "Settings saved successfully")}
                  </Alert>
                )}

                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-3 text-muted">{t("loading", "Loading configuration...")}</p>
                  </div>
                ) : (
                  <Form onSubmit={handleSave}>
                    {/* Wallet Address */}
                    <FormGroup>
                      <Label for="walletAddress" className="form-control-label">
                        <i className="fas fa-wallet mr-2"></i>
                        {t("walletAddress", "EVM Wallet Address")}
                      </Label>
                      <Input
                        type="text"
                        id="walletAddress"
                        name="walletAddress"
                        value={formData.walletAddress}
                        onChange={handleInputChange}
                        disabled={isSaving}
                        placeholder="0x..."
                        className="font-monospace"
                      />
                      <small className="form-text text-muted">
                        {t("walletAddressHelp", "This address will be used across all supported EVM chains")}
                      </small>
                    </FormGroup>

                    {/* Supported Chains */}
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        <i className="fas fa-link mr-2"></i>
                        {t("supportedChains", "Supported Blockchains")}
                      </Label>
                      <div className="d-flex flex-wrap gap-2">
                        {DEFAULT_CHAINS.map((chain) => (
                          <Button
                            key={chain.id}
                            type="button"
                            color={formData.supportedChains.includes(chain.id) ? "primary" : "secondary"}
                            outline={!formData.supportedChains.includes(chain.id)}
                            size="sm"
                            onClick={() => handleChainToggle(chain.id)}
                            disabled={isSaving}
                            className="mr-2 mb-2"
                          >
                            <i className={`${chain.icon} mr-1`}></i>
                            {chain.label}
                            {formData.supportedChains.includes(chain.id) && <i className="fas fa-check ml-2"></i>}
                          </Button>
                        ))}
                      </div>
                    </FormGroup>

                    {/* Supported Currencies */}
                    <FormGroup>
                      <Label className="form-control-label d-block">
                        <i className="fas fa-coins mr-2"></i>
                        {t("supportedCurrencies", "Accepted Currencies")}
                      </Label>
                      <div className="d-flex flex-wrap gap-2">
                        {DEFAULT_CURRENCIES.map((currency) => (
                          <Button
                            key={currency.id}
                            type="button"
                            color={formData.supportedCurrencies.includes(currency.id) ? "success" : "secondary"}
                            outline={!formData.supportedCurrencies.includes(currency.id)}
                            size="sm"
                            onClick={() => handleCurrencyToggle(currency.id)}
                            disabled={isSaving}
                            className="mr-2 mb-2"
                            style={{
                              borderColor: formData.supportedCurrencies.includes(currency.id)
                                ? currency.color
                                : undefined,
                              backgroundColor: formData.supportedCurrencies.includes(currency.id)
                                ? currency.color
                                : undefined,
                            }}
                          >
                            {currency.label}
                            {formData.supportedCurrencies.includes(currency.id) && (
                              <i className="fas fa-check ml-2"></i>
                            )}
                          </Button>
                        ))}
                      </div>
                    </FormGroup>

                    {/* Minimum Amount */}
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="minimumAmount" className="form-control-label">
                            <i className="fas fa-euro-sign mr-2"></i>
                            {t("minimumAmount", "Minimum Amount (EUR)")}
                          </Label>
                          <Input
                            type="number"
                            id="minimumAmount"
                            name="minimumAmount"
                            min="0"
                            step="0.01"
                            value={formData.minimumAmount}
                            onChange={handleInputChange}
                            disabled={isSaving}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Notes */}
                    <FormGroup>
                      <Label for="notes" className="form-control-label">
                        <i className="fas fa-sticky-note mr-2"></i>
                        {t("notes", "Payment Instructions")}
                      </Label>
                      <Input
                        type="textarea"
                        id="notes"
                        name="notes"
                        rows={3}
                        value={formData.notes}
                        onChange={handleInputChange}
                        disabled={isSaving}
                        placeholder={String(
                          t("notesPlaceholder", "Instructions shown to users when making crypto payments...")
                        )}
                      />
                    </FormGroup>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                      <div>
                        <Button color="primary" type="submit" disabled={isSaving || !hasChanges}>
                          {isSaving ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              {t("saving", "Saving...")}
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save mr-2"></i>
                              {t("saveChanges", "Save Changes")}
                            </>
                          )}
                        </Button>

                        {hasChanges && (
                          <Button
                            color="secondary"
                            type="button"
                            onClick={handleReset}
                            disabled={isSaving}
                            className="ml-2"
                          >
                            {t("reset", "Reset")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Preview Panel */}
          <Col xl={4}>
            <Card>
              <CardHeader>
                <h3 className="mb-0">
                  <i className="fas fa-eye mr-2"></i>
                  {t("preview", "Preview")}
                </h3>
              </CardHeader>
              <CardBody className="text-center">
                {previewLoading ? (
                  <div className="py-4">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : preview?.walletAddress ? (
                  <>
                    {/* Disabled warning */}
                    {!preview.isEnabled && (
                      <Alert color="warning" className="mb-3 text-left">
                        <i className="fas fa-pause-circle mr-2"></i>
                        {t("disabled", "Disabled")} - {t("previewOnly", "Preview only")}
                      </Alert>
                    )}

                    {/* QR Code */}
                    {preview.qrCodeDataUrl && (
                      <div className="mb-4">
                        <img
                          src={preview.qrCodeDataUrl}
                          alt="Wallet QR Code"
                          style={{
                            width: 180,
                            height: 180,
                            border: "4px solid #e9ecef",
                            borderRadius: 8,
                          }}
                        />
                      </div>
                    )}

                    {/* Wallet Address */}
                    <div className="bg-light rounded p-3 mb-3">
                      <small className="text-muted d-block mb-1">{t("walletAddress", "Wallet Address")}</small>
                      <code className="text-dark" style={{ fontSize: "0.75rem", wordBreak: "break-all" }}>
                        {preview.walletAddress}
                      </code>
                    </div>

                    {/* Supported Info */}
                    <div className="text-left">
                      <div className="mb-2">
                        <small className="text-muted">{t("chains", "Chains")}:</small>
                        <div className="mt-1">
                          {preview.supportedChains?.map((chain) => (
                            <span key={chain} className="badge badge-secondary mr-1 mb-1">
                              {chain}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted">{t("currencies", "Currencies")}:</small>
                        <div className="mt-1">
                          {preview.supportedCurrencies?.map((currency) => (
                            <span key={currency} className="badge badge-success mr-1">
                              {currency}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <small className="text-muted">{t("minimum", "Minimum")}:</small>
                        <span className="ml-2 font-weight-bold">{preview.minimumAmount} EUR</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="py-4 text-muted">
                    <i className="fas fa-wallet fa-3x mb-3 opacity-50"></i>
                    <p>{t("noPreview", "Configure a wallet address to see preview")}</p>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Info Card */}
            <Card>
              <CardHeader>
                <h3 className="mb-0">
                  <i className="fas fa-info-circle mr-2"></i>
                  {t("info", "Information")}
                </h3>
              </CardHeader>
              <CardBody>
                <ul className="text-sm mb-0 pl-3">
                  <li className="mb-2">
                    {t("info1", "The same wallet address works across all EVM-compatible chains")}
                  </li>
                  <li className="mb-2">{t("info2", "QR code is auto-generated from the wallet address")}</li>
                  <li className="mb-2">
                    {t("info3", "Admins can record crypto payments in the manual payments section")}
                  </li>
                  <li>{t("info4", "Only USDC and EURC stablecoins are supported")}</li>
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Password Confirmation Modal */}
      <Modal isOpen={showPasswordModal} toggle={handlePasswordModalClose} centered>
        <ModalHeader toggle={handlePasswordModalClose}>
          <i className="fas fa-shield-alt mr-2"></i>
          {t("password.title", "Confirm Wallet Address Change")}
        </ModalHeader>
        <ModalBody>
          <Alert color="warning" className="mb-4">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            {t(
              "password.warning",
              "You are about to change the payment wallet address. This is a sensitive operation that requires your password."
            )}
          </Alert>

          <div className="mb-3">
            <Label className="form-control-label">{t("password.newAddress", "New Wallet Address")}</Label>
            <code
              className="d-block bg-light p-2 rounded text-dark"
              style={{ fontSize: "0.8rem", wordBreak: "break-all" }}
            >
              {formData.walletAddress}
            </code>
          </div>

          {passwordError && (
            <Alert color="danger" className="mb-3">
              <i className="fas fa-times-circle mr-2"></i>
              {passwordError}
            </Alert>
          )}

          <FormGroup>
            <Label for="confirmPassword" className="form-control-label">
              <i className="fas fa-lock mr-2"></i>
              {t("password.label", "Enter Your Password")}
            </Label>
            <Input
              type="password"
              id="confirmPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("password.placeholder", "Your account password")}
              disabled={isSaving}
              onKeyDown={(e) => e.key === "Enter" && handlePasswordConfirm()}
              autoFocus
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handlePasswordModalClose} disabled={isSaving}>
            {t("password.cancel", "Cancel")}
          </Button>
          <Button color="primary" onClick={handlePasswordConfirm} disabled={isSaving || !password}>
            {isSaving ? (
              <>
                <Spinner size="sm" className="mr-2" />
                {t("password.confirming", "Confirming...")}
              </>
            ) : (
              <>
                <i className="fas fa-check mr-2"></i>
                {t("password.confirm", "Confirm Change")}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CryptoPaymentSettings;
